<template>
  <div class="import-wizard">
    <div class="stepper">
      <div class="stepper-header">
        <div :class="['step', currentStep >= 1 ? 'active' : '', currentStep > 1 ? 'completed' : '']">
          <div class="step-number">1</div>
          <div class="step-label">Upload CSV</div>
        </div>

        <div class="step-divider"></div>

        <div :class="['step', currentStep >= 2 ? 'active' : '', currentStep > 2 ? 'completed' : '']">
          <div class="step-number">2</div>
          <div class="step-label">Map Columns</div>
        </div>

        <div class="step-divider"></div>

        <!-- Dynamic steps based on what value mappings are needed -->
        <template v-if="showCourseTypeMappingStep && showInstructorMappingStep">
          <!-- Both mapping steps -->
          <div :class="['step', currentStep >= 3 ? 'active' : '', currentStep > 3 ? 'completed' : '']">
            <div class="step-number">3</div>
            <div class="step-label">Map Course Types</div>
          </div>
          <div class="step-divider"></div>
          <div :class="['step', currentStep >= 4 ? 'active' : '', currentStep > 4 ? 'completed' : '']">
            <div class="step-number">4</div>
            <div class="step-label">Map Instructors</div>
          </div>
          <div class="step-divider"></div>
          <div :class="['step', currentStep >= 5 ? 'active' : '', currentStep > 5 ? 'completed' : '']">
            <div class="step-number">5</div>
            <div class="step-label">Preview & Import</div>
          </div>
          <div class="step-divider"></div>
          <div :class="['step', currentStep >= 6 ? 'active' : '']">
            <div class="step-number">6</div>
            <div class="step-label">Results</div>
          </div>
        </template>

        <template v-else-if="showCourseTypeMappingStep || showInstructorMappingStep">
          <!-- One mapping step -->
          <div :class="['step', currentStep >= 3 ? 'active' : '', currentStep > 3 ? 'completed' : '']">
            <div class="step-number">3</div>
            <div class="step-label">{{ showCourseTypeMappingStep ? 'Map Course Types' : 'Map Instructors' }}</div>
          </div>
          <div class="step-divider"></div>
          <div :class="['step', currentStep >= 4 ? 'active' : '', currentStep > 4 ? 'completed' : '']">
            <div class="step-number">4</div>
            <div class="step-label">Preview & Import</div>
          </div>
          <div class="step-divider"></div>
          <div :class="['step', currentStep >= 5 ? 'active' : '']">
            <div class="step-number">5</div>
            <div class="step-label">Results</div>
          </div>
        </template>

        <template v-else>
          <!-- No mapping steps -->
          <div :class="['step', currentStep >= 3 ? 'active' : '', currentStep > 3 ? 'completed' : '']">
            <div class="step-number">3</div>
            <div class="step-label">Preview & Import</div>
          </div>
          <div class="step-divider"></div>
          <div :class="['step', currentStep >= 4 ? 'active' : '']">
            <div class="step-number">4</div>
            <div class="step-label">Results</div>
          </div>
        </template>
      </div>

      <div class="stepper-content">
        <!-- Step 1: Upload CSV -->
        <div v-if="currentStep === 1" class="step-content">
          <course-file-uploader
            @file-selected="handleFileSelected"
            @uploaded="handleFileUploaded"
          />

          <div class="actions">
            <button
              class="btn btn-primary"
              :disabled="!fileUploaded"
              @click="nextStep"
            >
              Continue
            </button>
          </div>
        </div>

        <!-- Step 2: Map Columns -->
        <div v-if="currentStep === 2" class="step-content">
          <course-column-mapper
            v-show="fileUploaded"
            :headers="csvHeaders"
            :preview-data="previewData"
            :available-fields="availableFields"
            ref="columnMapper"
          />

          <div class="actions">
            <button class="btn btn-secondary" @click="prevStep">Back</button>
            <button
              class="btn btn-primary"
              :disabled="!canProceedFromColumnMapping"
              :class="{ 'loading': analyzingFile }"
              @click="proceedFromColumnMapping"
            >
              <span v-if="analyzingFile">Analyzing...</span>
              <span v-else>{{ getNextStepLabel() }}</span>
            </button>
          </div>
        </div>

        <!-- Step 3: Map Course Types (conditional) -->
        <div v-if="currentStep === 3 && showCourseTypeMappingStep" class="step-content">
          <course-value-mapper
            title="Map Course Types"
            entity-label="course type"
            option-group-label="Course Types"
            :unique-values="uniqueCourseTypeValues"
            :available-options="availableCourseTypes"
            :mapping="courseTypeMapping"
            @update:mapping="updateCourseTypeMapping"
          />

          <div class="actions">
            <button class="btn btn-secondary" @click="prevStep">Back</button>
            <button
              class="btn btn-primary"
              :disabled="!canProceedFromCourseTypeMapping"
              @click="nextStep"
            >
              {{ showInstructorMappingStep ? 'Map Instructors' : 'Preview Import' }}
            </button>
          </div>
        </div>

        <!-- Step 3 or 4: Map Instructors (conditional) -->
        <div v-if="currentStep === instructorMappingStep && showInstructorMappingStep" class="step-content">
          <course-value-mapper
            title="Map Instructors"
            entity-label="instructor"
            option-group-label="Instructors"
            :unique-values="uniqueInstructorValues"
            :available-options="availableInstructors"
            :mapping="instructorMapping"
            @update:mapping="updateInstructorMapping"
          />

          <div class="actions">
            <button class="btn btn-secondary" @click="prevStep">Back</button>
            <button
              class="btn btn-primary"
              :disabled="!canProceedFromInstructorMapping"
              @click="nextStep"
            >
              Preview Import
            </button>
          </div>
        </div>

        <!-- Preview & Import step -->
        <div v-if="currentStep === previewStep" class="step-content">
          <course-import-preview
            :preview-data="mappedPreviewData"
            :options="importOptions"
            @update:options="updateImportOptions"
          />

          <div class="actions">
            <button class="btn btn-secondary" @click="prevStep">Back</button>
            <button
              class="btn btn-primary"
              :class="{ 'loading': importing }"
              @click="startImport"
            >
              <span v-if="importing">Importing...</span>
              <span v-else>Start Import</span>
            </button>
          </div>
        </div>

        <!-- Results step -->
        <div v-if="currentStep === resultsStep" class="step-content">
          <course-import-results
            :results="importResults"
            :status="importStatus"
            :progress="importProgress"
            :failure-reason="importFailureReason"
          />

          <div class="actions">
            <button class="btn btn-secondary" @click="resetWizard">
              Import Another File
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CourseFileUploader from './CourseFileUploader.vue';
import CourseColumnMapper from './CourseColumnMapper.vue';
import CourseValueMapper from './CourseValueMapper.vue';
import CourseImportPreview from './CourseImportPreview.vue';
import CourseImportResults from './CourseImportResults.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    CourseFileUploader,
    CourseColumnMapper,
    CourseValueMapper,
    CourseImportPreview,
    CourseImportResults
  },

  beforeDestroy() {
    // Clean up polling interval when component is destroyed
    this.stopPolling();
  },

  data() {
    return {
      fileId: null,
      // Polling configuration
      pollingInterval: null,
      pollAttempts: 0,
      maxPollAttempts: 300, // 10 minutes at 2s intervals
      pollIntervalMs: 2000, // 2 seconds
    }
  },

  computed: {
    ...mapGetters(['schoolId']),

    currentStep: {
      get() {
        return this.$store.state.courseImport.currentStep;
      },
      set(value) {
        this.$store.dispatch('courseImport/SET_COURSE_IMPORT_STEP', value);
      }
    },

    fileUploaded: {
      get() {
        return this.$store.state.courseImport.fileUploaded;
      },
      set(value) {
        this.$store.dispatch('courseImport/SET_COURSE_IMPORT_FILE_UPLOADED', value);
      }
    },

    csvHeaders() {
      return this.$store.state.courseImport.csvHeaders;
    },

    previewData() {
      return this.$store.state.courseImport.previewData;
    },

    availableFields() {
      return this.$store.state.courseImport.availableFields;
    },

    importOptions: {
      get() {
        return this.$store.state.courseImport.importOptions;
      },
      set(value) {
        this.$store.dispatch('courseImport/SET_COURSE_IMPORT_OPTIONS', value);
      }
    },

    importing() {
      return this.$store.state.courseImport.importing;
    },

    importResults() {
      return this.$store.state.courseImport.importResults;
    },

    mappedPreviewData() {
      return this.$store.getters['courseImport/courseImportMappedPreviewData'];
    },

    // Course type mapping state
    showCourseTypeMappingStep() {
      return this.$store.state.courseImport.showCourseTypeMappingStep;
    },

    availableCourseTypes() {
      return this.$store.state.courseImport.availableCourseTypes;
    },

    uniqueCourseTypeValues() {
      return this.$store.state.courseImport.uniqueCourseTypeValues;
    },

    courseTypeMapping() {
      return this.$store.state.courseImport.courseTypeMapping;
    },

    // Instructor mapping state
    showInstructorMappingStep() {
      return this.$store.state.courseImport.showInstructorMappingStep;
    },

    availableInstructors() {
      return this.$store.state.courseImport.availableInstructors;
    },

    uniqueInstructorValues() {
      return this.$store.state.courseImport.uniqueInstructorValues;
    },

    instructorMapping() {
      return this.$store.state.courseImport.instructorMapping;
    },

    analyzingFile() {
      return this.$store.state.courseImport.analyzingFile;
    },

    importStatus() {
      return this.$store.state.courseImport.importStatus;
    },

    importProgress() {
      return this.$store.state.courseImport.importProgress;
    },

    importFailureReason() {
      return this.$store.state.courseImport.importFailureReason;
    },

    // Dynamic step numbers based on which mapping steps are shown
    instructorMappingStep() {
      // If course type mapping is shown, instructor is step 4, otherwise step 3
      return this.showCourseTypeMappingStep ? 4 : 3;
    },

    previewStep() {
      if (this.showCourseTypeMappingStep && this.showInstructorMappingStep) {
        return 5;
      } else if (this.showCourseTypeMappingStep || this.showInstructorMappingStep) {
        return 4;
      }
      return 3;
    },

    resultsStep() {
      return this.previewStep + 1;
    },

    // Validation computed properties
    canProceedFromColumnMapping() {
      const mappedFields = Object.values(this.$store.state.courseImport.columnMapping);
      const requiredFields = this.$store.state.courseImport.requiredFields;
      return requiredFields.every((field) => mappedFields.includes(field));
    },

    canProceedFromCourseTypeMapping() {
      return this.$store.getters['courseImport/validateCourseImportCourseTypeMapping'];
    },

    canProceedFromInstructorMapping() {
      return this.$store.getters['courseImport/validateCourseImportInstructorMapping'];
    }
  },

  methods: {
    nextStep() {
      const maxStep = this.resultsStep;
      if (this.currentStep < maxStep) {
        this.currentStep++;
      }
    },

    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    resetWizard() {
      this.$store.dispatch('courseImport/RESET_COURSE_IMPORT');
    },

    handleFileSelected(file) {
      // Handle file selection logic
    },

    handleFileUploaded(response) {
      this.fileUploaded = true;
      this.fileId = response.file.id;

      this.$store.dispatch('courseImport/SET_COURSE_IMPORT_FILE_INFO', {
        directory: response.file.directory,
        name: response.file.name
      });

      // Fetch preview data
      this.$store.dispatch('courseImport/PREVIEW_COURSE_IMPORT_FILE', {school_id: this.schoolId, file_id: response.file.id})
      .then(response => {
        this.$store.dispatch('courseImport/SET_COURSE_IMPORT_PREVIEW_DATA', {
          headers: response.data.headers,
          data: response.data.sample_data,
          fields: response.data.available_fields
        });

        // Set required fields
        const requiredFields = response.data.available_fields
          .filter(field => field.required)
          .map(field => field.value);
        this.$store.dispatch('courseImport/SET_COURSE_IMPORT_REQUIRED_FIELDS', requiredFields);

        // Store available course types from the preview response
        if (response.data.course_types) {
          this.$store.dispatch('courseImport/SET_COURSE_IMPORT_AVAILABLE_COURSE_TYPES', response.data.course_types);
        }

        // Store available instructors from the preview response
        if (response.data.instructors) {
          this.$store.dispatch('courseImport/SET_COURSE_IMPORT_AVAILABLE_INSTRUCTORS', response.data.instructors);
        }

        this.nextStep();
      })
      .catch(error => {
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'error',
          message: 'Failed to load preview: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
        });
      });
    },

    updateImportOptions(options) {
      this.importOptions = { ...options };
    },

    updateCourseTypeMapping(mapping) {
      this.$store.dispatch('courseImport/SET_COURSE_IMPORT_COURSE_TYPE_MAPPING', mapping);
    },

    updateInstructorMapping(mapping) {
      this.$store.dispatch('courseImport/SET_COURSE_IMPORT_INSTRUCTOR_MAPPING', mapping);
    },

    getNextStepLabel() {
      const columnMapping = this.$store.state.courseImport.columnMapping;
      const mappedFields = Object.values(columnMapping);

      const courseTypeMapped = mappedFields.includes('COURSE_TYPE_NAME');
      const instructorMapped = mappedFields.includes('INSTRUCTOR_NAME');

      if (courseTypeMapped) {
        return 'Map Course Types';
      } else if (instructorMapped) {
        return 'Map Instructors';
      }
      return 'Preview Import';
    },

    proceedFromColumnMapping() {
      const columnMapping = this.$store.state.courseImport.columnMapping;
      const mappedFields = Object.values(columnMapping);

      // Check which fields are mapped
      const courseTypeMapped = mappedFields.includes('COURSE_TYPE_NAME');
      const instructorMapped = mappedFields.includes('INSTRUCTOR_NAME');

      if (courseTypeMapped || instructorMapped) {
        // Call analyze endpoint to get unique values
        this.$store.dispatch('courseImport/ANALYZE_COURSE_IMPORT_FILE', {
          school_id: this.schoolId,
          file_id: this.fileId,
          column_mapping: columnMapping
        })
        .then(response => {
          const uniqueValues = response.data.unique_values;

          // Handle course type values
          const courseTypeValues = uniqueValues['COURSE_TYPE_NAME'] || [];
          if (courseTypeMapped && courseTypeValues.length > 0) {
            this.$store.dispatch('courseImport/SET_COURSE_IMPORT_UNIQUE_COURSE_TYPE_VALUES', courseTypeValues);
            this.$store.dispatch('courseImport/SET_COURSE_IMPORT_SHOW_COURSE_TYPE_MAPPING_STEP', true);
          } else {
            this.$store.dispatch('courseImport/SET_COURSE_IMPORT_SHOW_COURSE_TYPE_MAPPING_STEP', false);
          }

          // Handle instructor values
          const instructorValues = uniqueValues['INSTRUCTOR_NAME'] || [];
          if (instructorMapped && instructorValues.length > 0) {
            this.$store.dispatch('courseImport/SET_COURSE_IMPORT_UNIQUE_INSTRUCTOR_VALUES', instructorValues);
            this.$store.dispatch('courseImport/SET_COURSE_IMPORT_SHOW_INSTRUCTOR_MAPPING_STEP', true);
          } else {
            this.$store.dispatch('courseImport/SET_COURSE_IMPORT_SHOW_INSTRUCTOR_MAPPING_STEP', false);
          }

          this.nextStep();
        })
        .catch(error => {
          this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
            type: 'error',
            message: 'Failed to analyze file: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
          });
        });
      } else {
        // Neither field mapped, skip to preview
        this.$store.dispatch('courseImport/SET_COURSE_IMPORT_SHOW_COURSE_TYPE_MAPPING_STEP', false);
        this.$store.dispatch('courseImport/SET_COURSE_IMPORT_SHOW_INSTRUCTOR_MAPPING_STEP', false);
        this.nextStep();
      }
    },

    startImport() {
      this.$store.dispatch('courseImport/SET_COURSE_IMPORT_IMPORTING', true);

      // Prepare value mappings for the API
      // Convert "skip" values to null for the backend
      const courseTypeMapping = {};
      const rawCourseTypeMapping = this.$store.state.courseImport.courseTypeMapping;
      Object.keys(rawCourseTypeMapping).forEach(key => {
        const value = rawCourseTypeMapping[key];
        courseTypeMapping[key] = value === 'skip' ? null : value;
      });

      const instructorMapping = {};
      const rawInstructorMapping = this.$store.state.courseImport.instructorMapping;
      Object.keys(rawInstructorMapping).forEach(key => {
        const value = rawInstructorMapping[key];
        instructorMapping[key] = value === 'skip' ? null : value;
      });

      this.$store.dispatch('courseImport/START_COURSE_IMPORT', {
        school_id: this.schoolId,
        file_id: this.fileId,
        test: this.importOptions.test,
        column_mapping: this.$store.state.courseImport.columnMapping,
        course_type_mapping: this.showCourseTypeMappingStep ? courseTypeMapping : undefined,
        instructor_mapping: this.showInstructorMappingStep ? instructorMapping : undefined
      })
      .then(response => {
        // Check if this is an async response (202 with import_id)
        if (response.status === 202 && response.data.import_id) {
          // Async import - store the import_id and start polling
          this.$store.dispatch('courseImport/SET_COURSE_IMPORT_ID', response.data.import_id);
          this.$store.dispatch('courseImport/SET_COURSE_IMPORT_STATUS', 'pending');
          this.nextStep(); // Move to results step to show progress
          this.startPolling(response.data.import_id);
        } else {
          // Synchronous response (test mode) - results returned immediately
          this.$store.dispatch('courseImport/SET_COURSE_IMPORT_RESULTS', response.data);
          this.$store.dispatch('courseImport/SET_COURSE_IMPORT_STATUS', 'completed');
          this.$store.dispatch('courseImport/SET_COURSE_IMPORT_IMPORTING', false);
          this.nextStep();
        }
      })
      .catch(error => {
        this.$store.dispatch('courseImport/SET_COURSE_IMPORT_IMPORTING', false);
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'error',
          message: 'Import failed: ' + (error.response?.data?.detail || error.response?.data?.error || 'Unknown error')
        });
      });
    },

    startPolling(importId) {
      this.pollAttempts = 0;
      this.pollingInterval = setInterval(() => {
        this.pollImportStatus(importId);
      }, this.pollIntervalMs);
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    pollImportStatus(importId) {
      this.pollAttempts++;

      // Safety check: stop polling after max attempts to prevent infinite loop
      if (this.pollAttempts >= this.maxPollAttempts) {
        this.stopPolling();
        this.$store.dispatch('courseImport/SET_COURSE_IMPORT_IMPORTING', false);
        this.$store.dispatch('courseImport/SET_COURSE_IMPORT_STATUS', 'failed');
        this.$store.dispatch('courseImport/SET_COURSE_IMPORT_FAILURE_REASON', 'Import timed out. Please check the import status manually or contact support.');
        this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
          type: 'error',
          message: 'Import polling timed out after 10 minutes.'
        });
        return;
      }

      this.$store.dispatch('courseImport/GET_COURSE_IMPORT_STATUS', {
        school_id: this.schoolId,
        import_id: importId
      })
      .then(response => {
        const status = response.data.status;

        if (status === 'completed' || status === 'failed') {
          this.stopPolling();
          this.$store.dispatch('courseImport/SET_COURSE_IMPORT_IMPORTING', false);

          if (status === 'completed') {
            this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
              type: 'success',
              message: 'Import completed successfully!'
            });
          } else {
            this.$store.dispatch('ui/ADD_FLASH_MESSAGE', {
              type: 'error',
              message: 'Import failed: ' + (response.data.failure_reason || 'Unknown error')
            });
          }
        }
      })
      .catch(error => {
        // On network error, don't immediately stop - allow some retries
        console.error('Polling error:', error);

        // Stop polling if we get a 404 (import not found) or other client error
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          this.stopPolling();
          this.$store.dispatch('courseImport/SET_COURSE_IMPORT_IMPORTING', false);
          this.$store.dispatch('courseImport/SET_COURSE_IMPORT_STATUS', 'failed');
          this.$store.dispatch('courseImport/SET_COURSE_IMPORT_FAILURE_REASON', 'Unable to retrieve import status.');
        }
      });
    }
  },
}
</script>

<style scoped>
.import-wizard {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell,
    'Open Sans', 'Helvetica Neue', sans-serif;
  max-width: 1200px;
  margin: 0 auto;
}

.stepper {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stepper-header {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  min-width: 80px;
  color: #757575;
}

.step.active {
  color: #1976d2;
}

.step.completed {
  color: #4caf50;
}

.step-number {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #fff;
  margin-bottom: 8px;
}

.step.active .step-number {
  background-color: #1976d2;
}

.step.completed .step-number {
  background-color: #4caf50;
}

.step-label {
  font-size: 12px;
  text-align: center;
  white-space: nowrap;
}

.step-divider {
  height: 1px;
  background-color: #e0e0e0;
  flex: 1;
  min-width: 20px;
  margin: 0 8px;
}

.stepper-content {
  padding: 24px;
}

.step-content {
  min-height: 400px;
  display: flex;
  flex-direction: column;
}

.actions {
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
}

.btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  transition: background-color 0.3s;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #1976d2;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #1565c0;
}

.btn-secondary {
  background-color: #f5f5f5;
  color: #757575;
}

.btn-secondary:hover {
  background-color: #e0e0e0;
}

.loading {
  position: relative;
  pointer-events: none;
}

.loading::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 1em;
  height: 1em;
  border-radius: 50%;
  border: 2px solid #fff;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
  transform: translate(-50%, -50%);
}

@keyframes spin {
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}
</style>
